<template>
    <data-table
        action-button-text="مشتری جدید"
        action-button-icon="add"
        additional-action-button-text="وارد کردن گروهی مشتریان"
        additional-action-button-icon="icon-download"
        show-select
        remove-key="user"
        export-key="customers"
        settings-key="customers"
        @click:additionalActionButton="importDialog = true"
        @click:actionButton="router.push({path: '/customers/new' })"
        @getList="getList"
        :rowClick="(row) => router.resolve({path: `/customers/${row.id}`})"
        :headers="headers"
        :items="items"
        :loading="loading"
        :totalItems="totalItems"
        :filters="filters"
        :actions="actions"
        :additionalExports="additionalExports"
        :getAllList="getAllList"
    >
        <template #item.tickets_recipient="{value}">
            <v-chip color="secondary" variant="flat" class="px-5 rounded text-small">{{value}}</v-chip>
        </template>
        <template #item.tickets_owner="{value}">
            <v-chip color="success" variant="flat" class="px-5 rounded text-small">{{value}}</v-chip>
        </template>
        <template #item.assigned_departments="{value}">
            <span v-if="value.length == 0">-------</span>
            <span v-else-if="value.length == 1">{{ system.departments.find(x => x.id == value[0].department_id).name }}</span>
            <v-menu v-else>
                <template #activator="{ props }">
                    <v-btn v-bind="props" icon="icon-orgg" color="primary" variant="text"></v-btn>
                </template>
                <div class="py-3 px-8 bg-white elevation-1">
                    <p v-for="item in value" class="text-small text-secondary font-weight-bold my-2">{{ system.departments.find(x => x.id == item.department_id).name }}</p>
                </div>
            </v-menu>
        </template>
        <template #item.assigned_services="{value}">
            <span v-if="value.length == 0">-------</span>
            <span v-else-if="value.length == 1">{{ system.services.find(x => x.id == value[0].service_id).name }}</span>
            <v-menu v-else>
                <template #activator="{ props }">
                    <v-btn v-bind="props" icon="icon-orgg" color="primary" variant="text"></v-btn>
                </template>
                <div class="py-3 px-8 bg-white elevation-1">
                    <p v-for="item in value" class="text-small text-secondary font-weight-bold my-2">{{ system.services.find(x => x.id == item.service_id).name }}</p>
                </div>
            </v-menu>
        </template>
        <template #item.custom_fields="{value, item}">
            <span v-if="value.length == 0">-------</span>
            <v-btn v-else icon="icon-relatedarticles" color="primary" variant="text" @click.stop="customFields = value; userName = item.name; customFieldShowRef.drawer = true"></v-btn>
        </template>
    </data-table>
    <custom-fields-show ref="customFieldShowRef" :title="`اطلاعات تکمیلی ${userName}`" :customFields="customFields"></custom-fields-show>
    <v-navigation-drawer color="#fafafa" temporary location="left" v-model="customerHistoryDrawer" width="400">
        <v-card tile color="transparent">
            <v-toolbar color="white">
                <v-btn @click="customerHistoryDrawer = false" icon>
                    <v-icon color="primary" size="23">close</v-icon>
                </v-btn>
                <v-toolbar-title class="text-primary">تاریخچه رویدادهای مشتری</v-toolbar-title>
            </v-toolbar>
            <v-divider></v-divider>
            <div class="px-2">
                <customer-event-sidebar :user="customerInformation" :key="customerInformation"></customer-event-sidebar>
            </div>
        </v-card>
    </v-navigation-drawer>
    <import-user-dialog v-model="importDialog"></import-user-dialog>
    <customer-call-dialog ref="customerCallDialogRef"></customer-call-dialog>
</template>
<script setup>
import { useApi } from '@/plugins/api';
import { useAuthStore } from '@/stores/auth';
import { useSystemStore } from '@/stores/system';
import { watch, onBeforeMount, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';

defineOptions({
    title: 'مشتریان',
    validate(context) {
        return context.rbac.can('manage customers');
    },
})
const api = useApi();
const router = useRouter();
const moment = useMoment();
const system = useSystemStore();
const auth = useAuthStore();
const rbac = useRbac();
const customerCallDialogRef = useTemplateRef('customerCallDialogRef');
const customFieldShowRef = useTemplateRef('customFieldShowRef');
const customFields = ref([]);
const userName = ref(null);
const customerInformation = ref(null);
const additionalExports = ref({});
const loading = ref(false);
const importDialog = ref(false);
const customerHistoryDrawer = ref(false);
const items = ref([]);
const totalItems = ref(0);
const headers = ref([
    {
        title: '',
        key: 'newTab',
        minWidth: 25,
        filterable: false,
        sortable: false,
        exportable: false,
        removeable: false,
        hideInMobile: true,
    },
    {
        title: 'نام',
        key: 'first_name',
        value: (item) => item.first_name,
        minWidth: 140,
    },
    {
        title: 'نام خانوادگی',
        key: 'last_name',
        value: (item) => item.last_name,
        minWidth: 160,
    },
    {
        title: 'شعبه',
        key: 'branch',
        value: (item) => system.branches.find(x => x.id == item.branch_id).name,
        minWidth: 170,
        permission: auth.user.full_admin,
    },
    {
        title: 'سطح مشتری',
        key: 'customer_level',
        value: (item) => item.customer_information.customer_level.name,
        minWidth: 160,
    },
    {
        title: 'سازمان مشتری',
        key: 'organization',
        value: (item) => item.customer_information.organization.name,
        minWidth: 190,
    },
    {
        title: 'سطح سازمان',
        key: 'organization_level',
        value: (item) => item.customer_information.organization.customer_level.name,
        minWidth: 160,
    },
    {
        title: 'سمت سازمانی',
        key: 'position',
        value: (item) => item.position,
        minWidth: 160,
    },
    {
        title: 'آمار تیکت ها',
        key: 'tickets',
        align: 'center',
        children: [
            {
                title: 'دریافتی',
                key: 'tickets_recipient',
                minWidth: 85,
                align: 'center',
                value: (item) => item.tickets.recipient ?? '0'
            },
            {
                title: 'ارسالی',
                key: 'tickets_owner',
                minWidth: 85,
                align: 'center',
                value: (item) => item.tickets.owner ?? '0'
            }
        ],
        filterable: false,
        sortable: false,
    },
    {
        title: 'نام کاربری',
        key: 'username',
        value: (item) => item.username,
        minWidth: 120,
    },
    {
        title: 'ایمیل',
        key: 'email',
        value: (item) => item.email,
        minWidth: 120,
    },
    {
        title: 'شماره موبایل',
        key: 'mobile',
        value: (item) => item.mobile,
        minWidth: 120,
    },
    {
        title: 'دپارتمان های اختصاصی',
        key: 'assigned_departments',
        exportValue: (item) => item.assigned_departments?.length ? item.assigned_departments.map(x => system.departments.find(y => y.id == x.department_id).name).join(', ') : undefined,
        minWidth: 100,
        filterable: false,
        sortable: false,
        permission: system.license.features.customer_department
    },
    {
        title: 'محصولات/سرویس‌های اختصاصی',
        key: 'assigned_services',
        exportValue: (item) => item.assigned_services?.length ? item.assigned_services.map(x => system.services.find(y => y.id == x.service_id).name).join(', ') : undefined,
        minWidth: 100,
        filterable: false,
        sortable: false,
        permission: system.license.features.customer_department
    },
    {
        title: 'فیلد های سفارشی',
        key: 'custom_fields',
        minWidth: 100,
        filterable: false,
        sortable: false,
        exportable: false,
        permission: system.license.features.customer_custom_field
    },
    {
        title: 'استان',
        key: 'state',
        value: (item) => item.customer_information.state_id && system.states.length ? system.states.find(x => x.id == item.customer_information.state_id).name : undefined,
        minWidth: 130,
    },
    {
        title: 'تلفن ثابت',
        key: 'landline',
        value: (item) => item.landline,
        minWidth: 120,
    },
    {
        title: 'تاریخ انقضا حساب',
        key: 'expire_date',
        cellProps: {style: 'direction: ltr; text-align: right'},
        value: (item) => item.customer_information.expire_date ? moment(item.customer_information.expire_date).format('jYYYY/jMM/jDD') : undefined,
        minWidth: 150,
    },
    {
        title: 'وضعیت',
        key: 'status',
        value: (item) => item.status ? 'فعال' : 'غیر فعال',
        minWidth: 70,
    },
]);
const filters = ref({
    page: { type: 'hidden', label: 'صفحه', value: 1 },
    perPage: { type: 'hidden', label: 'تعداد آیتم در صفحه', value: 10 },
    keyword_based: { type: 'divider', label: 'بر اساس کلمه کلیدی', value: null },
    keyword: { type: 'text', label: 'نام یا نام خانوادگی', value: null },
    description_keyword: { type: 'text', label: 'توضیحات', value: null },
    username: { type: 'text', label: 'نام کاربری', value: null },
    email: { type: 'text', label: 'ایمیل', value: null },
    mobile: { type: 'text', label: 'شماره موبایل', value: null },
    position_based: { type: 'divider', label: 'بر اساس موقعیت سازمانی', value: null },
    branches: { type: 'autocomplete', multiple: true, label: 'شعبه', items: system.branches, value: [], permission: auth.user.full_admin },
    organizations: { type: 'autocomplete', multiple: true, label: 'سازمان مشتری', items: system.organizations, value: [] },
    customerLevels: { type: 'autocomplete', multiple: true, label: 'سطح مشتری', items: system.customerLevels, value: [] },
    feature_based: { type: 'divider', label: 'بر اساس ویژگی های مشتری', value: null },
    assigned_department_ids: { type: 'autocomplete', multiple: true, label: 'دپارتمان های اختصاصی', items: system.departments.filter(x => ![system.customers_department.id, system.managers_department.id].includes(x.id)), value: [], permission: system.license.features.customer_department },
    assigned_service_ids: { type: 'autocomplete', multiple: true, label: 'محصولات/سرویس‌های اختصاصی', items: system.services, value: [], permission: system.license.features.customer_department },
    tags: { type: 'autocomplete', multiple: true, label: 'برچسب‌ها', items: [], value: [] },
    custom_field_based: { type: 'divider', label: 'بر اساس فیلد های سفارشی', value: null, permission: system.license.features.customer_custom_field },
    custom_field_items: { type: 'autocomplete', label: 'فیلد های سفارشی', multiple: true, items: system.customerCustomFields, itemTitle: 'label', value: [], permission: system.license.features.customer_custom_field },
    custom_fields: { type: 'customFields', multiple: true, label: 'فیلد های سفارشی', items: [], value: [], permission: system.license.features.customer_custom_field },
    expire_based: { type: 'divider', label: 'بر اساس تاریخ انقضا حساب', value: null },
    from_expire: { type: 'date', label: 'تاریخ انقضا', group: 'start', onChange: (value) => { filters.value.to_expire.value = value > filters.value.to_expire.value ? null : filters.value.to_expire.value; filters.value.to_expire.min = value; }, value: null },
    to_expire: { type: 'date', label: 'تاریخ انقضا', group: 'end', min: null, value: null },
    status_based: { type: 'divider', label: 'بر اساس وضعیت', value: null },
    status: { type: 'autocomplete', label: 'وضعیت', items: [{ name: 'فعال', id: '1' },{ name: 'غیرفعال', id: '0' }], value: null },
});
const actions = ref([
    {
        title: 'لیست تیکت ها',
        icon: 'icon-listticket',
        clicked: (selected) => router.push({path: '/tickets', query: { customer_id: selected[0].id}}),
        enabled: (selected) => selected.length === 1
    },
    {
        title: 'ارسال تیکت به مشتری',
        icon: 'icon-newticket',
        clicked: (selected) => router.push({ path: '/tickets/new', query: auth.user.full_admin ?
            { customer_ids: selected.map((x) => x.id).join(','), branch_id: selected[0].branch_id } :
            { customer_ids: selected.map((x) => x.id).join(',') } }),
        enabled: (selected) => selected.length > 0 &&
            rbac.can('send ticket to customers') &&
            (!auth.user.full_admin ||
                !selected.find((x) => x.branch_id != selected[0].branch_id))
    },
    {
        title: 'ارسال تیکت از طرف مشتری',
        icon: 'icon-comment-user',
        clicked: (selected) => router.push({path: '/tickets/fromCustomer', query: { customer_id: selected[0].id } }),
        enabled: (selected) => selected.length === 1 &&
            system.license.features.ticket_send_behalf_customer &&
            rbac.can('send ticket behalf customers')
    },
    {
        title: 'تاریخچه رویدادها',
        icon: 'icon-historyevent',
        clicked: (selected) => { customerInformation.value = selected[0]; customerHistoryDrawer.value = true },
        enabled: (selected) => selected.length === 1 &&
            system.license.features.customer_event
    },
    {
        title: 'تماس با مشتری',
        icon: 'icon-mobile',
        clicked: (selected) => { customerCallDialogRef.value.calculateNumbers(selected[0]); customerCallDialogRef.value.dialog = true },
        enabled: (selected) => selected.length === 1 &&
            customerCallDialogRef.value.userHasNumber(selected[0])
    },
    {
        title: 'ویراش مشتری',
        icon: 'icon-editt',
        clicked: (selected) => router.push({path: '/customers/edit', query: { id: selected[0].id } }),
        enabled: (selected) => selected.length === 1
    },
]);
async function getList(finalFilters, cb = false) {
    loading.value = true;
    await api.userList(finalFilters).then(async res => {
        if (res && res.status == 'OK') {
            items.value = res.data;
            totalItems.value = res.total;
            if (system.license.features.customer_custom_field)
                additionalExports.value = res.data.map(x => x.custom_fields).flat().filter((item, index, all) => all.findIndex(x => x.id == item.id) == index).reduce((res, item) => (res[item.label] = x => x.custom_fields.find(x => x.id == item.id) ? customFieldShowRef.value.getValue(x.custom_fields.find(x => x.id == item.id)) : undefined, res), {});
        }
    }).finally(() => loading.value = false);
    if (cb)
        cb();
}
async function getAllList(finalFilters) {
    let page = 1, data = [];
    while (page) {
        await api.userList({
            ...finalFilters,
            perPage: 200,
            page: page,
        }).then(async res => {
            if (res && res.status == 'OK') {
                data = data.concat(res.data);
                page = page == res.totalPages ? false : page + 1
            } else
                page = false;
        }).catch(() => page = false);
    }
    if (system.license.features.customer_custom_field)
        additionalExports.value = data.map(x => x.custom_fields).flat().filter((item, index, all) => all.findIndex(x => x.id == item.id) == index).reduce((res, item) => (res[item.label] = x => x.custom_fields.find(x => x.id == item.id) ? customFieldShowRef.value.getValue(x.custom_fields.find(x => x.id == item.id)) : undefined, res), {});
    return data;
}
watch(filters.value.custom_field_items, () => {
    filters.value.custom_fields.items = system.customerCustomFields.filter(x => filters.value.custom_field_items.value.some(y => y == x.id));
});
onBeforeMount(() => {
    system.setTags().then(() => filters.value.tags.items = system.tags);
});
</script>
